import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersAPI, resourcesAPI } from '../utils/api';
import SkillInput from '../components/SkillInput';
import AvailabilityPicker from '../components/AvailabilityPicker';
import './Profile.css';

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resources, setResources] = useState([]);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [form, setForm] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    skillsOffered: user?.skillsOffered || [],
    skillsWanted: user?.skillsWanted || [],
    availability: user?.availability || [],
  });

  const [resourceForm, setResourceForm] = useState({ title: '', description: '', type: 'note', content: '', skill: '' });
  const [resourceLoading, setResourceLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        bio: user.bio || '',
        location: user.location || '',
        skillsOffered: user.skillsOffered || [],
        skillsWanted: user.skillsWanted || [],
        availability: user.availability || [],
      });
    }
  }, [user]);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const { data } = await resourcesAPI.getAll();
      setResources(data);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await usersAPI.updateMe(form);
      await refreshUser();
      setEditing(false);
      setSuccess('Profile updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddResource = async () => {
    if (!resourceForm.title || !resourceForm.content || !resourceForm.skill) {
      return;
    }
    setResourceLoading(true);
    try {
      await resourcesAPI.create(resourceForm);
      await fetchResources();
      setResourceForm({ title: '', description: '', type: 'note', content: '', skill: '' });
      setShowResourceModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setResourceLoading(false);
    }
  };

  const handleDeleteResource = async (id) => {
    try {
      await resourcesAPI.delete(id);
      setResources(r => r.filter(x => x._id !== id));
    } catch {}
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  if (!user) return null;

  return (
    <div className="profile-page">
      <div className="page-container">
        <div className="profile-layout">
          <aside className="profile-sidebar">
            <div className="card profile-avatar-card">
              <div className="avatar" style={{ width: 80, height: 80, fontSize: 28, margin: '0 auto 16px' }}>
                {user.avatar
                  ? <img src={user.avatar} alt={user.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                  : initials}
              </div>
              <h2 className="profile-name">{user.name}</h2>
              {user.location && <p className="profile-location">📍 {user.location}</p>}
              {user.bio && <p className="profile-bio">{user.bio}</p>}
              <div className="profile-stats">
                <div className="stat">
                  <span className="stat-num">{user.skillsOffered?.length || 0}</span>
                  <span className="stat-label">Offering</span>
                </div>
                <div className="stat">
                  <span className="stat-num">{user.skillsWanted?.length || 0}</span>
                  <span className="stat-label">Wanting</span>
                </div>
                <div className="stat">
                  <span className="stat-num">{user.totalSwaps || 0}</span>
                  <span className="stat-label">Swaps</span>
                </div>
              </div>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setEditing(!editing)}>
                {editing ? 'Cancel Edit' : 'Edit Profile'}
              </button>
            </div>

            <div className="card" style={{ padding: 20 }}>
              <h4 style={{ marginBottom: 12, fontSize: 14, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Member since</h4>
              <p style={{ fontSize: 14, color: 'var(--gray-700)' }}>
                {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </aside>

          <main className="profile-main">
            {success && <div className="success-banner">✓ {success}</div>}
            {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

            <div className="profile-tabs">
              {['overview', 'resources'].map(tab => (
                <button
                  key={tab}
                  className={`profile-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <>
                {editing ? (
                  <div className="card" style={{ padding: 24 }}>
                    <h3 style={{ marginBottom: 20 }}>Edit Profile</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div className="form-group">
                        <label className="form-label">Name</label>
                        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Location</label>
                        <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="City, Country" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Bio</label>
                        <textarea rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} style={{ resize: 'vertical' }} />
                      </div>
                      <SkillInput label="Skills I Offer" skills={form.skillsOffered} onChange={v => setForm(f => ({ ...f, skillsOffered: v }))} />
                      <SkillInput label="Skills I Want" skills={form.skillsWanted} onChange={v => setForm(f => ({ ...f, skillsWanted: v }))} />
                      <AvailabilityPicker availability={form.availability} onChange={v => setForm(f => ({ ...f, availability: v }))} />
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
                        <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? <><span className="loading-spinner" /> Saving…</> : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="card" style={{ padding: 24, marginBottom: 20 }}>
                      <h3 style={{ marginBottom: 16 }}>Skills I Offer</h3>
                      <div className="skills-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {user.skillsOffered?.length > 0
                          ? user.skillsOffered.map((s, i) => <span key={i} className="tag">{s}</span>)
                          : <span style={{ color: 'var(--gray-400)', fontSize: 14 }}>No skills added yet</span>}
                      </div>
                    </div>

                    <div className="card" style={{ padding: 24, marginBottom: 20 }}>
                      <h3 style={{ marginBottom: 16 }}>Skills I Want</h3>
                      <div className="skills-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {user.skillsWanted?.length > 0
                          ? user.skillsWanted.map((s, i) => <span key={i} className="tag tag-wanted">{s}</span>)
                          : <span style={{ color: 'var(--gray-400)', fontSize: 14 }}>No skills added yet</span>}
                      </div>
                    </div>

                    <div className="card" style={{ padding: 24 }}>
                      <h3 style={{ marginBottom: 16 }}>Availability</h3>
                      {user.availability?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {user.availability.map((a, i) => (
                            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-500)', width: 90 }}>{a.day}</span>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {a.slots.map((s, j) => (
                                  <span key={j} className="badge badge-primary">{s}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--gray-400)', fontSize: 14 }}>No availability set</span>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {activeTab === 'resources' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3>My Resources</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowResourceModal(true)}>+ Add Resource</button>
                </div>

                {resources.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📚</div>
                    <h3>No resources yet</h3>
                    <p>Add notes, links, or files to share with your swap partners</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {resources.map(r => (
                      <div key={r._id} className="card resource-item">
                        <div className="resource-header">
                          <div>
                            <h4 className="resource-title">{r.title}</h4>
                            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                              <span className="badge badge-primary">{r.skill}</span>
                              <span className="badge badge-gray">{r.type}</span>
                            </div>
                          </div>
                          {r.uploader?._id === user._id && (
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteResource(r._id)}>Delete</button>
                          )}
                        </div>
                        {r.description && <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8 }}>{r.description}</p>}
                        {r.type === 'link' && (
                          <a href={r.content} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--primary-dark)', marginTop: 8, display: 'block' }}>
                            🔗 {r.content}
                          </a>
                        )}
                        {r.type === 'note' && (
                          <p style={{ fontSize: 13, background: 'var(--gray-50)', padding: '10px 12px', borderRadius: 8, marginTop: 8, color: 'var(--gray-700)', lineHeight: 1.5 }}>
                            {r.content}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {showResourceModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowResourceModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>Add Resource</h3>
              <button className="close-btn" onClick={() => setShowResourceModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input value={resourceForm.title} onChange={e => setResourceForm(f => ({ ...f, title: e.target.value }))} placeholder="Resource title" />
              </div>
              <div className="form-group">
                <label className="form-label">Skill</label>
                <input value={resourceForm.skill} onChange={e => setResourceForm(f => ({ ...f, skill: e.target.value }))} placeholder="Related skill" />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select value={resourceForm.type} onChange={e => setResourceForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="note">Note</option>
                  <option value="link">Link</option>
                  <option value="file">File (base64)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{resourceForm.type === 'link' ? 'URL' : 'Content'}</label>
                <textarea rows={4} value={resourceForm.content} onChange={e => setResourceForm(f => ({ ...f, content: e.target.value }))} placeholder={resourceForm.type === 'link' ? 'https://...' : 'Write your note here...'} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <input value={resourceForm.description} onChange={e => setResourceForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowResourceModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddResource} disabled={resourceLoading}>
                {resourceLoading ? <><span className="loading-spinner" /> Adding…</> : 'Add Resource'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
